// Opening hours. The backend reads them from the Hours block of the home
// page and serves them in `@restaurant-settings` as `opening_hours`.

export interface TimeRange {
  opens: string;
  closes: string;
  service?: string;
}

export interface OpeningDay {
  day: string;
  label: string;
  closed: boolean;
  hours: TimeRange[];
}

const DAY_NAMES: Record<string, string> = {
  lundi: 'Monday',
  mardi: 'Tuesday',
  mercredi: 'Wednesday',
  jeudi: 'Thursday',
  vendredi: 'Friday',
  samedi: 'Saturday',
  dimanche: 'Sunday',
};

const SHORT_LABELS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

// A service that opens before this time is a lunch service.
const EVENING_FROM = '16:00';

// Same rule as TIME_RANGE in the backend module opening_hours.py. The editor
// uses it only to warn about a time that the backend cannot read.
const TIME_RANGE =
  /(\d{1,2})\s*[:hH]\s*(\d{0,2})\s*[-–—]\s*(\d{1,2})\s*[:hH]\s*(\d{0,2})/;

const normalize = (text: string | undefined | null): string =>
  (text || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const parseDay = (label: string | undefined | null): string | null =>
  DAY_NAMES[normalize(label)] || null;

export const isClosedText = (text: string | undefined | null): boolean =>
  normalize(text) === 'ferme';

const clock = (hours: string, minutes: string): string | null => {
  const hour = Number(hours);
  const minute = Number(minutes || '0');
  if (hour > 24 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const parseTimeRange = (
  text: string | undefined | null,
): TimeRange | null => {
  const match = (text || '').match(TIME_RANGE);
  if (!match) return null;
  const opens = clock(match[1], match[2]);
  const closes = clock(match[3], match[4]);
  if (!opens || !closes) return null;
  return { opens, closes };
};

export interface HoursRow {
  day?: string;
  time?: string;
  closed?: boolean;
}

// Tells the editor what the backend cannot read in a row of a service.
export const rowProblem = (row: HoursRow): 'day' | 'time' | null => {
  if (!row.day?.trim() && !row.time?.trim()) return null;
  if (!parseDay(row.day)) return 'day';
  if (row.closed || isClosedText(row.time)) return null;
  if (!parseTimeRange(row.time)) return 'time';
  return null;
};

// "mer. → dim." for three days or more in a row, "sam., dim." for two.
const formatDays = (open: boolean[]): string => {
  if (open.every(Boolean)) return 'tous les jours';
  const start = open.findIndex((isOpen) => !isOpen);
  const runs: number[][] = [];
  let run: number[] = [];
  for (let step = 1; step <= 7; step += 1) {
    const index = (start + step) % 7;
    if (open[index]) {
      run.push(index);
    } else if (run.length) {
      runs.push(run);
      run = [];
    }
  }
  return runs
    .map((days) => {
      const first = SHORT_LABELS[days[0]];
      const last = SHORT_LABELS[days[days.length - 1]];
      if (days.length === 1) return first;
      if (days.length === 2) return `${first}, ${last}`;
      return `${first} → ${last}`;
    })
    .join(', ');
};

// The service lines of the hero and the footer, such as
// ["Midi · mer. → dim.", "Soir · mer. → sam."].
export const serviceLines = (days: OpeningDay[] | undefined): string[] => {
  if (!Array.isArray(days) || days.length !== 7) return [];
  const lunch = days.map((day) =>
    day.hours.some((range) => range.opens < EVENING_FROM),
  );
  const evening = days.map((day) =>
    day.hours.some((range) => range.opens >= EVENING_FROM),
  );
  const lines: string[] = [];
  if (lunch.some(Boolean)) lines.push(`Midi · ${formatDays(lunch)}`);
  if (evening.some(Boolean)) lines.push(`Soir · ${formatDays(evening)}`);
  return lines;
};

// schema.org OpeningHoursSpecification items. Days with the same time range
// share one item.
export const openingHoursSpecification = (
  days: OpeningDay[] | undefined,
): Record<string, unknown>[] => {
  if (!Array.isArray(days)) return [];
  const groups = new Map<string, { range: TimeRange; days: string[] }>();
  days.forEach((day) => {
    day.hours.forEach((range) => {
      const key = `${range.opens}-${range.closes}`;
      const group = groups.get(key) || { range, days: [] };
      group.days.push(day.day);
      groups.set(key, group);
    });
  });
  return Array.from(groups.values()).map((group) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: group.days,
    opens: group.range.opens,
    closes: group.range.closes,
  }));
};

// "Permanence téléphonique : mardi au samedi, 10h–19h". French writes
// lowercase after a colon.
export const phoneDeskText = (
  phoneHours: string | undefined | null,
): string => {
  const text = (phoneHours || '').trim();
  if (!text) return '';
  return `Permanence téléphonique : ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};
